import { fromUtf8, utf8 } from './bytes.js';
import { signCommand, verifyCommandCore, } from './command.js';
import { exactKeys, openContent, positiveInteger, sealContent, validateContentContext, } from './content.js';
import { canonicalize } from './jcs.js';
/** Encrypt first, then sign the ciphertext hash using the existing command policy. */
export async function signEncryptedCommand(input, device, content, options = {}) {
    const id = input.id ?? globalThis.crypto.randomUUID();
    const context = {
        version: 1,
        accountId: content.accountId,
        epoch: content.epoch,
        objectId: id,
        runId: input.machineId,
        seq: 1,
        kind: 'command',
    };
    const box = await sealContent(content.key, context, utf8(canonicalize(input.payload)));
    return signCommand({ ...input, id, payload: { context: { ...context }, box: { ...box } } }, device, options);
}
/** Signature, expiry, step-up and nonce policy run BEFORE touching any decryption key. */
export async function verifyEncryptedCommand(signed, options, content) {
    const verified = await verifyCommandCore(signed, options, true);
    if (!verified.ok)
        return verified;
    let result;
    try {
        positiveInteger(content.minimumEpoch);
        if (!verified.payload ||
            typeof verified.payload !== 'object' ||
            Array.isArray(verified.payload))
            throw new Error('encrypted payload required');
        exactKeys(verified.payload, ['context', 'box']);
        const encrypted = verified.payload;
        const c = encrypted.context;
        validateContentContext(c);
        if (c.accountId !== content.accountId ||
            c.objectId !== verified.envelope.id ||
            c.runId !== verified.envelope.machineId ||
            c.kind !== 'command' ||
            c.seq !== 1 ||
            c.epoch < content.minimumEpoch)
            throw new Error('wrong command context');
        const plain = await openContent(await content.key(c.epoch), c, encrypted.box);
        const payload = JSON.parse(fromUtf8(plain));
        canonicalize(payload);
        const { statementPending: _pending, ...rest } = verified;
        result = { ...rest, payload };
    }
    catch {
        // Fixed error text: never echo decrypted command content or key material to the relay.
        return {
            ok: false,
            reason: 'payload-mismatch',
            detail: 'encrypted payload authentication failed',
        };
    }
    if ('statementPending' in verified && result.ok) {
        // account-projects: the step-up is the passkey-approved statement inside the payload.
        const approved = await options.statementStepUp(result.envelope, result.payload);
        if (!approved.ok)
            return { ok: false, reason: 'bad-step-up', detail: approved.detail ?? 'statement' };
        return {
            ...result,
            stepUpCredentialId: approved.credentialId,
            ...(approved.grantId ? { stepUpGrantId: approved.grantId } : {}),
        };
    }
    return result;
}
//# sourceMappingURL=encrypted-command.js.map